import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { aiEmployees, aiEmployeeState } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import {
  Strategy, EnvelopeSimple, TrendUp, Package, ShareNetwork, ChartLineUp, UserCircle, ImageSquare, VideoCamera,
  Play, Pause, Prohibit, ArrowRight, Sparkle, CheckCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const ICONS = { Strategy, EnvelopeSimple, TrendUp, Package, ShareNetwork, ChartLineUp, UserCircle, ImageSquare, VideoCamera };
const STATUS = {
  running: { label: "Running", dot: "bg-emerald-400", text: "text-emerald-400" },
  paused: { label: "Paused", dot: "bg-amber-400", text: "text-amber-300" },
  disabled: { label: "Disabled", dot: "bg-red-500", text: "text-red-400" },
};

function Card({ e }) {
  const { activeId } = useClient();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const Icon = ICONS[e.icon] || Sparkle;
  const st = STATUS[e.status] || STATUS.running;
  const m = useMutation({
    mutationFn: (status) => aiEmployeeState({ client_id: activeId, key: e.key, status }),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ["ai-employees", activeId] }); toast.success(`${e.name} ${r.status}`); },
    onError: (x) => toast.error(x?.response?.data?.detail || "Failed"),
  });
  return (
    <div data-testid={`employee-${e.key}`} className="card-matte p-5">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2.5">
          <div className="h-10 w-10 rounded-xl bg-[#6B3DFF]/12 flex items-center justify-center"><Icon size={20} className="brand-text" weight="bold" /></div>
          <div>
            <div className="font-head font-semibold text-sm">{e.name}</div>
            <div className={`flex items-center gap-1.5 text-[11px] ${st.text}`} data-testid={`employee-status-${e.key}`}><span className={`h-1.5 w-1.5 rounded-full ${st.dot}`} /> {st.label}</div>
          </div>
        </div>
        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-white/8 text-[#cfcfcf]">{e.performance}</span>
      </div>

      <div className="mt-4 space-y-1.5 text-xs">
        <div className="flex items-center gap-1.5 text-[#cfcfcf]"><Sparkle size={11} className="brand-text" weight="fill" /> {e.current_task}</div>
        <div className="flex items-center justify-between text-[#7a7a7a]">
          <span className="flex items-center gap-1"><CheckCircle size={11} /> {e.completed} completed</span>
          <span>{e.pending} pending</span>
        </div>
        {e.last_activity ? <div className="text-[10px] text-[#5a5a5a]">Last activity: {new Date(e.last_activity).toLocaleString()}</div> : null}
      </div>

      <div className="flex items-center gap-1.5 mt-4 pt-3 border-t border-white/8">
        {e.status !== "running" && <button data-testid={`emp-run-${e.key}`} onClick={() => m.mutate("running")} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-emerald-500/30 text-emerald-400"><Play size={11} /> Run</button>}
        {e.status === "running" && <button data-testid={`emp-pause-${e.key}`} onClick={() => m.mutate("paused")} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-amber-500/30 text-amber-300"><Pause size={11} /> Pause</button>}
        {e.status !== "disabled" && <button data-testid={`emp-disable-${e.key}`} onClick={() => m.mutate("disabled")} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-white/8 text-[#9a9a9a] hover:text-red-400"><Prohibit size={11} /> Disable</button>}
        <button data-testid={`emp-open-${e.key}`} onClick={() => navigate(e.route)} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-white/8 text-[#9a9a9a] hover:text-white ml-auto">Open <ArrowRight size={11} /></button>
      </div>
    </div>
  );
}

export default function AiEmployeesPage() {
  const { activeId, activeClient } = useClient();
  const q = useQuery({ queryKey: ["ai-employees", activeId], queryFn: () => aiEmployees(activeId), enabled: !!activeId });
  const running = (q.data || []).filter((e) => e.status === "running").length;
  return (
    <PageContainer>
      <PageHeader eyebrow="AI Workforce" title="AI Employees"
        subtitle={`Your AI team working ${activeClient?.name || "this workspace"} — each specialist runs approval-first. ${running} running.`} />
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="ai-employees-grid">
        {(q.data || []).map((e) => <Card key={e.key} e={e} />)}
      </div>
    </PageContainer>
  );
}
